import { SelectionModel } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin, Observable } from 'rxjs';
import { OrganisationService } from '~core/data/organisation.service';
import { Industry } from '~core/models/industry.model';
import { Organisation } from '~core/models/organistion.model';
import { OrganisationDetailsComponent } from './organisation-details/organisation-details.component';
import { OrganisationEditDialogComponent } from './organisation-edit/organisation-edit-dialog.component';

@Component({
  selector: 'idl-organisations-list',
  templateUrl: './organisations.component.html',
  styleUrls: ['./organisations.component.scss']
})
export class OrganisationsComponent implements AfterViewInit {
  displayedColumns: string[] = [
    'select',
    'name',
    'industries',
    'comments',
    'crmLink'
  ];

  dataSource = new MatTableDataSource<Organisation>();
  selection = new SelectionModel<Organisation>(true, []);

  pipe: DatePipe;

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  isLoading = true;

  constructor(
    private _dialog: MatDialog,
    private _organisationService: OrganisationService
  ) {
    this.pipe = new DatePipe('en-US');
    this.dataSource = new MatTableDataSource();

    this.dataSource.filterPredicate = (data: Organisation, filters: string) => {
      const matchFilter = [];
      // will split by comma and upto 1 space
      const filterArray = filters.split(/,\s?/);

      // const columns = [data.name, data.race, data.color];
      // Or if you don't want to specify specifics columns =>
      // const columns = (<any>Object).values(data);

      // Main loop
      filterArray.forEach(filter => {
        const customFilter = [];

        // Get only viewed columns from data
        // Assumes view isn't displaying more trhen 1 data field
        this.displayedColumns.forEach(columnName => {
          const column = data[columnName];
          if (column) {
            if (column instanceof Date) {
              const pipedDate = this.pipe.transform(column, 'mediumDate');
              // And the piped date contains the value
              customFilter.push(pipedDate.toLowerCase().includes(filter));
            } else if (column instanceof Array) {
              customFilter.push(
                column.filter((industry: Industry) =>
                  industry.name.toLowerCase().includes(filter)
                ).length > 0
              );
            } else {
              customFilter.push(
                column
                  .toString()
                  .toLowerCase()
                  .includes(filter)
              );
            }
          }
        });

        matchFilter.push(customFilter.some(Boolean)); // OR
      });
      return matchFilter.every(Boolean); // AND
    };
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    /* now it's okay to set large data source... */
    this._organisationService.list().subscribe(data => {
      this.isLoading = false;
      this.dataSource.data = data.sort((a, b) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0
      );
    });
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${
      this.selection.isSelected(row) ? 'deselect' : 'select'
    } row ${row.position + 1}`;
  }

  applyFilter(event: KeyboardEvent) {
    this.dataSource.filter = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
  }

  removeSelectedRows() {
    const removes: Observable<any>[] = [];
    this.selection.selected.forEach(org => {
      const index = this.dataSource.data.findIndex(
        i => i.organisationId === org.organisationId
      );
      removes.push(this._organisationService.delete(org.organisationId));
      this.dataSource.data.splice(index, 1);
    });
    forkJoin(removes).subscribe(() => {
      this.dataSource.data = this.dataSource.data;
      this.selection.clear();
    });
  }

  addOrganisation() {
    const organisation = new Organisation();
    organisation.industries = [];

    const dlg = this._dialog.open(OrganisationEditDialogComponent, {
      minWidth: '400px',
      data: organisation
    });

    dlg.afterClosed().subscribe(res => {
      if (res) {
        this.dataSource.data.push(res);
        this.dataSource.data = this.dataSource.data;
      }
    });
  }

  onEditClick(organisation: Organisation): void {
    this._dialog.open(OrganisationEditDialogComponent, {
      minWidth: '400px',
      data: organisation
    });
  }

  onDetailsClick(organisation): void {
    this._dialog.open(OrganisationDetailsComponent, {
      minWidth: '400px',
      data: organisation
    });
  }
}
